/**
 * World Cup fantasy: the nation-based rules layered on top of the shared squad
 * model - a per-nation cap and the "at least one Arab nation" rule - plus the
 * config the WC season seeds into `fantasy_seasons.formation_rules`. Pure and
 * framework-free so it stays unit-testable; the server RPC re-validates.
 */
import { DEFAULT_SCORING_RULES, type FormationRules, type Position } from '@scoring/rules.ts';

import type { PlayerWithTeam } from '@/lib/data/provider';

import type { DraftIssue, SquadDraft } from './squadModel';
import { POSITIONS, countByPosition, validateDraft } from './squadModel';

/**
 * 3-letter FIFA/API-Football team codes of Arab nations - drives the
 * "≥1 Arab player" rule. These match `teams.short_name` from the API-Football
 * backfill (NOT ISO-3166 alpha-2). The eight currently in the WC 2026 pool are
 * ALG, EGY, IRQ, JOR, KSA, MAR, QAT, TUN; the rest are listed so the rule keeps
 * working if the pool ever widens.
 */
export const ARAB_NATION_CODES = new Set([
  // In the WC 2026 pool today:
  'ALG', 'EGY', 'IRQ', 'JOR', 'KSA', 'MAR', 'QAT', 'TUN',
  // Other Arab nations (future-proofing):
  'UAE', 'KUW', 'BHR', 'OMA', 'LBN', 'LBY', 'PLE', 'SDN', 'SYR', 'YEM',
  'MTN', 'COM', 'DJI', 'SOM',
]);

/**
 * The WC season formation/scoring config (seeded into the season row). The
 * starting XI is a fixed 4-4-2 with named slots (like the reference game) for a
 * clean, recognisable pitch; the squad carries a real 4-player bench so Bench
 * Boost and auto-subs are meaningful.
 */
export const WORLD_CUP_FORMATION_RULES: FormationRules = {
  squad: { GK: 2, DEF: 5, MID: 5, FWD: 3 },
  starting: { size: 11, GK: [1, 1], DEF: [4, 4], MID: [4, 4], FWD: [2, 2] },
  scoring: DEFAULT_SCORING_RULES,
};

export const WORLD_CUP_BUDGET = 100;

/**
 * Named-slot layout for the fixed 4-4-2 pitch, left→right within each row.
 * Display only - the engine works in GK/DEF/MID/FWD buckets; these label the
 * starters in order so the pitch reads LB·LCB·RCB·RB, LW·LCM·RCM·RW, ST·ST.
 */
export const SLOT_LABELS_442: Record<Position, string[]> = {
  GK: ['GK'],
  DEF: ['LB', 'LCB', 'RCB', 'RB'],
  MID: ['LW', 'LCM', 'RCM', 'RW'],
  FWD: ['ST', 'ST'],
};

/** Nation-diversity rules unique to the World Cup draft (not in the shared rules). */
export interface WcDraftRules {
  /** Max players drawn from one nation. */
  maxPerNation: number;
  /** Min players whose nation is Arab. */
  minArabNations: number;
  /** Free re-spins in the Randomizer before a nation must be accepted. */
  reSpins: number;
}

export const WORLD_CUP_DRAFT_RULES: WcDraftRules = {
  maxPerNation: 3,
  minArabNations: 1,
  reSpins: 3,
};

export type WcDraftIssue =
  | DraftIssue
  | { type: 'nationCap'; nationId: string; nationName: string; max: number }
  | { type: 'needArab'; count: number };

const nationId = (player: PlayerWithTeam): string | null => player.team?.id ?? null;

/** Players drawn from each nation, keyed by nation (team) id. */
export function countByNation(draft: SquadDraft): Map<string, number> {
  const counts = new Map<string, number>();
  for (const pick of draft.picks) {
    const id = nationId(pick.player);
    if (id) counts.set(id, (counts.get(id) ?? 0) + 1);
  }
  return counts;
}

/** Nation ids that have already reached the per-nation cap. */
export function nationsAtCap(draft: SquadDraft, max: number): Set<string> {
  const full = new Set<string>();
  for (const [id, count] of countByNation(draft)) {
    if (count >= max) full.add(id);
  }
  return full;
}

/** How many picks belong to an Arab nation (membership supplied by the caller,
 * since the seeded nation rows - not the player - carry the country code). */
export function arabNationCount(draft: SquadDraft, arabNationIds: Set<string>): number {
  return draft.picks.filter((pick) => {
    const id = nationId(pick.player);
    return id != null && arabNationIds.has(id);
  }).length;
}

/** Whether a nation can still take another pick under the per-nation cap. */
export function canAddFromNation(draft: SquadDraft, nationIdValue: string, max: number): boolean {
  return (countByNation(draft).get(nationIdValue) ?? 0) < max;
}

/**
 * All blocking issues for a World Cup draft: the shared position/budget/
 * formation/captaincy checks, plus the per-nation cap and the Arab-nation
 * minimum. Empty array = saveable.
 */
export function wcValidateDraft(
  draft: SquadDraft,
  rules: FormationRules,
  budget: number,
  wc: WcDraftRules,
  nationNames: Map<string, string>,
  arabNationIds: Set<string>,
): WcDraftIssue[] {
  const issues: WcDraftIssue[] = [...validateDraft(draft, rules, budget)];

  for (const [id, count] of countByNation(draft)) {
    if (count > wc.maxPerNation) {
      issues.push({
        type: 'nationCap',
        nationId: id,
        nationName: nationNames.get(id) ?? id,
        max: wc.maxPerNation,
      });
    }
  }

  const arab = arabNationCount(draft, arabNationIds);
  if (arab < wc.minArabNations) {
    issues.push({ type: 'needArab', count: wc.minArabNations - arab });
  }

  return issues;
}

// ---------------------------------------------------------------------------
// Randomizer engine - pure draft logic for the "spin a nation, then pick a
// bucket + player" mode. rng is injected so the draw is unit-testable.
// ---------------------------------------------------------------------------

const pickedIds = (draft: SquadDraft): Set<string> =>
  new Set(draft.picks.map((pick) => pick.player.id));

/** Remaining squad slots per position (squad quota minus current picks). */
export function positionNeeds(draft: SquadDraft, rules: FormationRules): Record<Position, number> {
  const counts = countByPosition(draft);
  const needs: Record<Position, number> = { GK: 0, DEF: 0, MID: 0, FWD: 0 };
  for (const position of POSITIONS) {
    needs[position] = Math.max(0, rules.squad[position] - counts[position]);
  }
  return needs;
}

/** Positions that still need at least one more player. */
export function neededPositions(draft: SquadDraft, rules: FormationRules): Position[] {
  const needs = positionNeeds(draft, rules);
  return POSITIONS.filter((position) => needs[position] > 0);
}

/** Whether every squad slot is filled. */
export function isDraftComplete(draft: SquadDraft, rules: FormationRules): boolean {
  return neededPositions(draft, rules).length === 0;
}

/** Unpicked pool players of a given nation + position. */
export function availablePlayers(
  draft: SquadDraft,
  pool: PlayerWithTeam[],
  nationIdValue: string,
  position: Position,
): PlayerWithTeam[] {
  const taken = pickedIds(draft);
  return pool.filter(
    (player) => player.team?.id === nationIdValue && player.position === position && !taken.has(player.id),
  );
}

/** Positions a nation can still supply, limited to the squad's open needs. */
export function neededPositionsForNation(
  draft: SquadDraft,
  pool: PlayerWithTeam[],
  rules: FormationRules,
  nationIdValue: string,
): Position[] {
  return neededPositions(draft, rules).filter(
    (position) => availablePlayers(draft, pool, nationIdValue, position).length > 0,
  );
}

/**
 * Nation ids that can still contribute a pick: under the per-nation cap and
 * holding an unpicked player for a position the squad still needs.
 */
export function eligibleNations(
  draft: SquadDraft,
  pool: PlayerWithTeam[],
  rules: FormationRules,
  wc: WcDraftRules,
): string[] {
  const atCap = nationsAtCap(draft, wc.maxPerNation);
  const ids = new Set<string>();
  for (const player of pool) {
    const id = player.team?.id;
    if (!id || atCap.has(id) || ids.has(id)) continue;
    if (neededPositionsForNation(draft, pool, rules, id).length > 0) ids.add(id);
  }
  return [...ids];
}

/** Draw a random eligible nation for the Randomizer, or null if none remain. */
export function spinNation(
  draft: SquadDraft,
  pool: PlayerWithTeam[],
  rules: FormationRules,
  wc: WcDraftRules,
  rng: () => number = Math.random,
): string | null {
  const nations = eligibleNations(draft, pool, rules, wc);
  if (nations.length === 0) return null;
  return nations[Math.floor(rng() * nations.length)] ?? null;
}
